import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { MapPin, Shield, Sparkles, Lock } from "lucide-react";
import { useGeolocation, getStoredConsent } from "@/hooks/useGeolocation";
import { toast } from "@/hooks/use-toast";

interface GeoConsentDialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onGranted?: () => void;
  /** If true, the dialog auto-opens the first time the user lands and has no decision yet. */
  autoPrompt?: boolean;
}

const GeoConsentDialog = ({ open, onOpenChange, onGranted, autoPrompt = false }: GeoConsentDialogProps) => {
  const { requestLocation, decline, loading } = useGeolocation();
  const [internalOpen, setInternalOpen] = useState(false);

  const isControlled = open !== undefined;
  const isOpen = isControlled ? open! : internalOpen;
  const setOpen = (v: boolean) => {
    if (!isControlled) setInternalOpen(v);
    onOpenChange?.(v);
  };

  useEffect(() => {
    if (autoPrompt && !isControlled && getStoredConsent() === "unknown") {
      setInternalOpen(true);
    }
  }, [autoPrompt, isControlled]);

  const handleAllow = async () => {
    // Pre-check browser permission — if blocked at OS/browser level, getCurrentPosition never prompts.
    try {
      if (navigator.permissions && (navigator.permissions as any).query) {
        const status = await navigator.permissions.query({ name: "geolocation" as PermissionName });
        if (status.state === "denied") {
          toast({
            title: "Géolocalisation bloquée par le navigateur",
            description:
              "Cliquez sur l'icône 🔒 à gauche de la barre d'adresse → Autorisations du site → Localisation → Autoriser, puis rechargez la page.",
            variant: "destructive",
          });
          return;
        }
      }
    } catch {
      // permissions API not available — proceed
    }

    const result = await requestLocation();
    if (result) {
      toast({
        title: "Position activée 📍",
        description: "Nous trouverons les gardes proches de vous.",
      });
      onGranted?.();
      setOpen(false);
    } else {
      toast({
        title: "Impossible d'obtenir votre position",
        description:
          "Vérifiez que la localisation est autorisée pour ce site dans votre navigateur, puis réessayez.",
        variant: "destructive",
      });
    }
  };

  const handleDecline = () => {
    decline();
    toast({
      title: "Géolocalisation refusée",
      description: "Vous pourrez toujours rechercher par ville ou code postal.",
    });
    setOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setOpen}>
      <DialogContent className="max-w-md rounded-2xl p-0 overflow-hidden">
        <div className="bg-gradient-to-br from-primary/15 via-primary/5 to-transparent px-6 pt-6 pb-4">
          <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10 mb-3">
            <MapPin className="w-7 h-7 text-primary" />
          </div>
          <DialogHeader className="text-left space-y-1.5">
            <DialogTitle className="text-xl">Trouver les gardes proches de vous</DialogTitle>
            <DialogDescription className="text-sm leading-relaxed">
              Autorisez l'accès à votre position pour découvrir instantanément les professionnels disponibles autour de chez vous.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-6 py-4 space-y-3">
          <div className="flex gap-3 items-start">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <Sparkles className="w-4 h-4 text-primary" />
            </div>
            <div className="text-sm">
              <p className="font-medium">Résultats personnalisés</p>
              <p className="text-muted-foreground text-xs">Les pros les plus proches en premier.</p>
            </div>
          </div>
          <div className="flex gap-3 items-start">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <Lock className="w-4 h-4 text-primary" />
            </div>
            <div className="text-sm">
              <p className="font-medium">Position approximative (~500&nbsp;m)</p>
              <p className="text-muted-foreground text-xs">
                Votre position exacte n'est jamais enregistrée — uniquement une zone élargie pour préserver votre vie privée.
              </p>
            </div>
          </div>
          <div className="flex gap-3 items-start">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <Shield className="w-4 h-4 text-primary" />
            </div>
            <div className="text-sm">
              <p className="font-medium">Conforme RGPD</p>
              <p className="text-muted-foreground text-xs">
                Choix mémorisé sur votre appareil, révocable à tout moment dans les paramètres.
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="px-6 pb-6 pt-2 flex-col-reverse sm:flex-col-reverse gap-2 sm:gap-2 sm:space-x-0">
          <Button
            variant="ghost"
            onClick={handleDecline}
            disabled={loading}
            className="w-full text-muted-foreground hover:text-foreground"
          >
            Plus tard
          </Button>
          <Button
            onClick={handleAllow}
            disabled={loading}
            className="w-full h-11 text-base font-semibold"
          >
            <MapPin className="w-4 h-4 mr-2" />
            {loading ? "Localisation…" : "Autoriser maintenant"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GeoConsentDialog;
